import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { authAPI, uploadAPI } from '@/services/api'
import paypalService from '@/services/paypal'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const toast = useToast()
  
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token') || null)
  const isLoading = ref(false)
  
  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const userPlan = computed(() => user.value?.subscription_plan || 'free')
  const isPremium = computed(() => userPlan.value === 'premium')
  const subscriptionStatus = computed(() => user.value?.subscription_status || 'active')
  const billingCycle = computed(() => user.value?.billing_cycle || 'monthly')
  
  // Actions
  const login = async (credentials) => {
    isLoading.value = true
    try {
      const response = await authAPI.login(credentials)
      const { user: userData, token: authToken } = response.data.data
      
      user.value = userData
      token.value = authToken
      localStorage.setItem('auth_token', authToken)
      localStorage.setItem('user_data', JSON.stringify(userData))
      
      toast.success('Login successful!')
      router.push('/dashboard')
    } catch (error) {
      const message = error.response?.data?.errors?.message || 'Login failed. Please try again.'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  
  const register = async (userData) => {
    isLoading.value = true
    try {
      const response = await authAPI.register(userData)
      const { user: newUser, token: authToken } = response.data.data
      
      user.value = newUser
      token.value = authToken
      localStorage.setItem('auth_token', authToken)
      localStorage.setItem('user_data', JSON.stringify(newUser))
      
      toast.success('Registration successful!')
      router.push('/dashboard')
    } catch (error) {
      const message = error.response?.data?.errors?.message || 'Registration failed. Please try again.'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  
  const logout = () => {
    user.value = null
    token.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    
    toast.info('Logged out successfully')
    router.push('/')
  }
  
  const initAuth = async () => {
    const savedUser = localStorage.getItem('user_data')
    const savedToken = localStorage.getItem('auth_token')
    
    if (savedUser && savedToken) {
      user.value = JSON.parse(savedUser)
      token.value = savedToken
      
      // Verify token is still valid
      try {
        const response = await authAPI.me()
        user.value = response.data.data
      } catch (error) {
        // Token is invalid, clear auth data
        logout()
      }
    }
  }
  
  const updateProfile = async (profileData) => {
    isLoading.value = true
    try {
      const response = await authAPI.updateProfile(profileData)
      const updatedUser = response.data.data
      
      user.value = updatedUser
      localStorage.setItem('user_data', JSON.stringify(updatedUser))
      
      toast.success('Profile updated successfully!')
      return updatedUser
    } catch (error) {
      const message = error.response?.data?.errors?.message || 'Failed to update profile'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  
  const deleteAccount = async () => {
    isLoading.value = true
    try {
      await authAPI.deleteAccount()
      
      // Clear all user data
      user.value = null
      token.value = null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      
      toast.success('Account deleted successfully')
      router.push('/')
    } catch (error) {
      const message = error.response?.data?.errors?.message || 'Failed to delete account'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  
  const processPayPalPayment = async (paymentResult) => {
    try {
      if (paymentResult.success && paymentResult.user) {
        // Update user data from PayPal payment response
        user.value = paymentResult.user
        localStorage.setItem('user_data', JSON.stringify(paymentResult.user))
        
        toast.success('Payment successful! Your plan has been upgraded.')
        return paymentResult
      } else {
        throw new Error('Payment was not successful')
      }
    } catch (error) {
      console.error('Failed to process PayPal payment:', error)
      toast.error('Failed to process payment. Please contact support.')
      throw error
    }
  }

  const cancelSubscription = async () => {
    isLoading.value = true
    try {
      const result = await paypalService.cancelSubscription()

      if (result.success) {
        // Subscription cancelled successfully
        // Note: User data will be updated by webhook or on next refresh
        return result
      } else {
        throw new Error('Failed to cancel subscription')
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to cancel subscription'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const getPaymentHistory = async () => {
    try {
      return await paypalService.getPaymentHistory()
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to load payment history'
      toast.error(message)
      throw error
    }
  }

  const forgotPassword = async (email) => {
    isLoading.value = true
    try {
      await authAPI.forgotPassword(email)
      toast.success('Password reset email sent! Check your inbox.')
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to send password reset email'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (token, password, passwordConfirmation) => {
    isLoading.value = true
    try {
      await authAPI.resetPassword(token, password, passwordConfirmation)
      toast.success('Password reset successfully! Please login with your new password.')
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to reset password'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const verifyEmail = async (verificationToken) => {
    isLoading.value = true
    try {
      const response = await authAPI.verifyEmail(verificationToken)
      const updatedUser = response.data.data
      
      user.value = updatedUser
      localStorage.setItem('user_data', JSON.stringify(updatedUser))
      
      toast.success('Email verified successfully!')
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to verify email'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const resendVerification = async () => {
    isLoading.value = true
    try {
      await authAPI.resendVerification()
      toast.success('Verification email sent!')
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to send verification email'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const loginWithGoogle = () => {
    // Directly redirect to backend OAuth endpoint
    // Backend will handle the redirect to Google
    authAPI.googleAuth()
  }

  const handleGoogleCallback = async (code, state) => {
    isLoading.value = true
    try {
      // Validate state for CSRF protection
      const storedState = sessionStorage.getItem('oauth_state')
      if (state !== storedState) {
        throw new Error('Invalid OAuth state')
      }
      
      const response = await authAPI.googleCallback(code, state)
      const { user: userData, token: authToken } = response.data.data
      
      user.value = userData
      token.value = authToken
      localStorage.setItem('auth_token', authToken)
      localStorage.setItem('user_data', JSON.stringify(userData))
      
      // Clean up
      sessionStorage.removeItem('oauth_state')
      
      toast.success('Successfully signed in with Google!')
      router.push('/dashboard')
    } catch (error) {
      const message = error.response?.data?.error || 'Google OAuth login failed'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const unlinkGoogle = async () => {
    isLoading.value = true
    try {
      const response = await authAPI.unlinkGoogle()
      const updatedUser = response.data.data
      
      user.value = updatedUser
      localStorage.setItem('user_data', JSON.stringify(updatedUser))
      
      toast.success('Google account unlinked successfully!')
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to unlink Google account'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const uploadAvatar = async (file) => {
    isLoading.value = true
    try {
      // Upload file to get URL (no profile update here)
      const uploadResponse = await uploadAPI.uploadImage(file)

      if (uploadResponse.data?.success && uploadResponse.data?.url) {
        return { success: true, url: uploadResponse.data.url }
      } else {
        const errorMsg = uploadResponse.data?.error || 'Avatar upload failed'
        return { success: false, error: errorMsg }
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Avatar upload failed'
      return { success: false, error: message }
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (currentPassword, newPassword, passwordConfirmation) => {
    isLoading.value = true
    try {
      await authAPI.changePassword(currentPassword, newPassword, passwordConfirmation)
      toast.success('Password changed successfully!')
    } catch (error) {
      // Handle different error response formats
      let message = 'Failed to change password'

      if (error.response?.status === 401) {
        // Current password is incorrect
        message = 'Current password is incorrect'
      } else if (error.response?.data?.errors?.message) {
        message = error.response.data.errors.message
      } else if (error.response?.data?.error) {
        message = error.response.data.error
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      }

      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const generateRandomString = () => {
    const array = new Uint8Array(32)
    crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
  }

  return {
    // State
    user,
    token,
    isLoading,

    // Getters
    isAuthenticated,
    userPlan,
    isPremium,
    subscriptionStatus,
    billingCycle,

    // Actions
    login,
    register,
    logout,
    initAuth,
    updateProfile,
    uploadAvatar,
    deleteAccount,
    changePassword,
    processPayPalPayment,
    cancelSubscription,
    getPaymentHistory,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    loginWithGoogle,
    handleGoogleCallback,
    unlinkGoogle
  }
})